import React from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl, defineMessages} from 'react-intl'
import {useGlobalBadges} from '../../contexts/global-elements-context'
import {resolveBadgeCmsStyles} from '../../utils/global-elements-utils'

const messages = defineMessages({
    bestseller: {
        id: 'product_badge.bestseller',
        defaultMessage: 'Bestseller'
    },
    almost_gone: {
        id: 'product_badge.almost_gone',
        defaultMessage: 'Almost Gone'
    }
})

const Badges = ({badges = [], badgesContainerStyleProps = {}, badgesStylesProps = {}}) => {
    const intl = useIntl()
    const {data: globalBadges} = useGlobalBadges()

    // Map badge IDs from product format (camelCase) to CMS configuration IDs.
    // Only badges present in this map are supported.
    const badgeIdMap = {
        isBestseller: 'bestseller',
        isAlmostGone: 'almost_gone'
    }

    // Resolve badge and configId before hooks that depend on them
    const badge = Array.isArray(badges) ? badges[0] : badges?.badges?.[0]
    const rawId = badge?.id
    const configId = badgeIdMap[rawId] ?? null

    // useMultiStyleConfig must be called unconditionally (rules of hooks)
    const styles = useMultiStyleConfig('ProductBadges', {variant: configId})

    // Early exits after all hooks
    if (!badge || !configId) return null

    // Find the corresponding badge configuration from Optimizely (optional override)
    const badgeConfig = globalBadges?.find((b) => b.id === configId)

    const {text, textStyles = [], borderRadius, hideBlockOn, opacity} = badgeConfig || {}

    // Resolve CMS textStyles into Chakra-ready override objects.
    // Handles breakpoint inheritance, FontSize token routing, opacity normalisation, and hide logic.
    const {containerOverrides: cmsContainerOverrides, badgeOverrides: cmsBadgeOverrides, hideStyles} =
        resolveBadgeCmsStyles(textStyles, borderRadius, opacity, hideBlockOn)

    if (hideStyles.doNotLoadComponent) return null

    return (
        <Box {...styles.container} {...cmsContainerOverrides} {...hideStyles.sx && {sx: hideStyles.sx}} {...badgesContainerStyleProps}>
            <Box
                {...styles.badge}
                key={rawId}
                data-testid="product-badge"
                {...cmsBadgeOverrides}
                {...badgesStylesProps}
            >
                {text || intl.formatMessage(messages[configId])}
            </Box>
        </Box>
    )
}

Badges.propTypes = {
    badges: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.shape({
                id: PropTypes.string.isRequired,
                contentId: PropTypes.object
            })
        ),
        PropTypes.shape({
            badges: PropTypes.arrayOf(
                PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    contentId: PropTypes.object
                })
            )
        })
    ]),
    badgesContainerStyleProps: PropTypes.object,
    badgesStylesProps: PropTypes.object
}

export default Badges
